import { prefixClass } from 'lib/utils';
import './STBTools.scss';
import { FilterKAI, IconNavQueue, IconRefresh, IconEditKai } from 'components/Icons';
import STBIcon from '../STBIcon';
import classNames from 'classnames';
import { SyntheticEvent } from 'react';
import KSExport from 'presentation/components/KSExport';
import { useReportsStore } from 'hooks/useReportsStore';
import { useFlags } from 'launchdarkly-react-client-sdk';

export type ActionProps = {
  tooltip: string;
  handleClick: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  icon: JSX.Element;
  disabled?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
};

type STBToolsProps = {
  adminMode?: boolean;
  status?: HoneReportStatus;
  report?: NestedHoneReport;
  onRefresh?: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  onAIReport?: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  onAIEditor?: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  onReportQueue?: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  onDownload?: (e: SyntheticEvent<HTMLButtonElement, Event>) => void;
  adminTools?: ActionProps[];
};

const STBTools = ({ adminMode = false, report, status = 'Published', ...events }: STBToolsProps) => {
  const prefix = prefixClass('STB-Tools');
  const { onRefresh, onAIReport, onAIEditor, onReportQueue, onDownload, adminTools } = events;
  const { enableAIReport } = useReportsStore();
  const { enableQueueReport } = useFlags();
  const adminAllowed = adminMode && adminTools?.length;

  // To avoid drilling down properties from the parent component
  // it only affects the aiReport property
  const { aiReport, reportQueue } = useReportsStore(state => ({
    aiReport: state.aiReport,
    reportQueue: state.reportQueue,
  }));

  const drawCommonTools = () => (
    <div className={classNames(prefix('common'), { admin: adminAllowed })}>
      {onRefresh && (
        <STBIcon
          tooltip="Refresh Report"
          handleClick={(e: SyntheticEvent<HTMLButtonElement, Event>) => onRefresh(e)}
          icon={<IconRefresh />}
        />
      )}
      {onAIReport && enableAIReport && (
        <>
          <STBIcon
            tooltip={aiReport ? 'Hide AI Report' : 'Show AI Report'}
            handleClick={(e: SyntheticEvent<HTMLButtonElement, Event>) => {
              // Check if shift key was pressed during click
              if (e.nativeEvent instanceof MouseEvent && e.nativeEvent.shiftKey) {
                onAIEditor && onAIEditor(e);
              } else {
                // Regular click without shift key
                onAIReport && onAIReport(e);
              }
            }}
            icon={<FilterKAI />}
            offState={!aiReport}
          />
        </>
      )}
      {onReportQueue && enableQueueReport && (
        <STBIcon
          tooltip={reportQueue ? 'Hide Report Queue' : 'Show Report Queue'}
          handleClick={(e: SyntheticEvent<HTMLButtonElement, Event>) => onReportQueue(e)}
          icon={<IconNavQueue />}
          offState={!reportQueue}
        />
      )}
      {onDownload && <KSExport report={report} />}
    </div>
  );

  const drawAdminTools = () => (
    <div className={prefix('admin')}>
      {adminTools &&
        adminTools.map((action, index) => (
          <STBIcon
            key={index}
            tooltip={action.tooltip}
            handleClick={action.handleClick}
            icon={action.icon}
            disabled={action.disabled}
            placement={action.placement}
          />
        ))}
    </div>
  );

  return (
    <>
      {report && (
        <div className={prefix()}>
          {drawCommonTools()}
          {adminAllowed && drawAdminTools()}
        </div>
      )}
    </>
  );
};
export default STBTools;
